import { create } from "zustand";
import { Dashboard } from "api/model/dashboard/Dashboard";
import { ContextConfig, DashboardTabModel } from "api/model/dashboard/DashboardTab";
import { IDashboardComponent } from "api/model/dashboard/DashboardComponent";
import { v4 } from "uuid";

interface DashboardState {
  dashboard: Dashboard | undefined;
  tab: DashboardTabModel | undefined;
  isEditMode: boolean;
  fromHistory: boolean;
  isGridChanged: boolean;
  tabId: number;
  refreshKey: number;
  saveKey: number;
  tabContextConfigs: ContextConfig[];
  isGroupReorderEnabled: boolean;
  selectedChartsIdentifiers: string[];
  setSelectedChartsIdentifiers: (selectedChartsIdentifiers: string[]) => void;
  setDashboard: (dashboard: Dashboard) => void;
  setTab: (tab: DashboardTabModel) => void;
  setIsEditMode: (isEditMode: boolean) => void;
  setFromHistory: (fromHistory: boolean) => void;
  setIsGridChanged: (isGridChanged: boolean) => void;
  triggerTabRefresh: () => void;
  triggerDashboardSave: () => void;
  getTab: (tabId: number) => DashboardTabModel | undefined;
  getTabContextConfig: (tabId: number) => any;
  setTabContextConfig: (tabId: number, tabContextConfig: ContextConfig) => void;
  updateComponent: (
    groupId: string,
    componentId: string,
    config: any,
    tabId: number,
    refresh?: boolean
  ) => void;
  removeComponent: (groupId: string, componentId: string, tabId: number) => void;
  setIsGroupReorderEnabled: (isGroupReorderEnabled: boolean) => void;
}

export const useDashboardStore = create<DashboardState>((set, get) => ({
  dashboard: undefined,
  tab: undefined,
  isEditMode: false,
  fromHistory: false,
  isGridChanged: false,
  tabId: 0,
  refreshKey: 0,
  saveKey: 0,
  tabContextConfigs: [],
  isGroupReorderEnabled: false,
  selectedChartsIdentifiers: [],

  setSelectedChartsIdentifiers: (selectedChartsIdentifiers: string[]) => set({ selectedChartsIdentifiers }),
  setDashboard: (dashboard: Dashboard) => set({ dashboard }),
  setTab: (tab: any) => set({ tab }),
  setIsEditMode: (isEditMode: boolean) => set({ isEditMode }),
  setFromHistory: (fromHistory: boolean) => set({ fromHistory }),
  setIsGridChanged: (isGridChanged: boolean) => set({ isGridChanged }),
  triggerTabRefresh: () => set(state => ({ refreshKey: state.refreshKey + 1 })),
  triggerDashboardSave: () => set(state => ({ saveKey: state.saveKey + 1 })),
  getTab: (tabId: number) => get().dashboard?.dashboardTabsJs?.[tabId],
  getTabContextConfig: (tabId: number) =>
    get().tabContextConfigs[tabId] || get().dashboard?.dashboardTabsJs?.[tabId]?.contextConfig,
  setTabContextConfig: (tabId: number, config: ContextConfig) =>
    set(state => {
      const newContextConfig = { ...state.tabContextConfigs, [tabId]: config };
      return { tabContextConfigs: newContextConfig };
    }),
  setIsGroupReorderEnabled: (isGroupReorderEnabled: boolean) => set({ isGroupReorderEnabled }),
  updateComponent: (
    groupId: string,
    componentId: string,
    config: any,
    tabId: number,
    refresh = false
  ) => {
    set(state => {
      const { dashboard } = state;
      if (!dashboard || !dashboard.dashboardTabsJs?.[tabId]) return state;

      const newDashboard = { ...dashboard };
      const tab = newDashboard.dashboardTabsJs[tabId];
      const group = tab.componentGroups?.find(g => g.id === groupId);
      if (!group) return state;

      const updatedComponents: IDashboardComponent[] = group.components.map(component =>
        component.id === componentId
          ? { ...component, ...config, id: refresh ? v4() : component.id }
          : component
      );

      const updatedGroup = { ...group, components: updatedComponents };
      const updatedGroups = tab.componentGroups.map(g => (g.id === groupId ? updatedGroup : g));

      newDashboard.dashboardTabsJs[tabId] = { ...tab, componentGroups: updatedGroups };

      return {
        dashboard: newDashboard,
      };
    });
  },
  removeComponent: (groupId: string, componentId: string, tabId: number) => {
    const state = get();
    const { dashboard } = state;
    if (!dashboard) return;
    const tab = dashboard.dashboardTabsJs?.[tabId];
    const group = tab?.componentGroups?.find(g => g.id === groupId);
    if (!group) return;
    const compIndex = group.components.findIndex(component => component.id === componentId);
    if (compIndex === -1) return;
    group.components.splice(compIndex, 1);
    set({ dashboard, refreshKey: state.refreshKey + 1 });
  },
}));

export const setEditMode = (isEdit: boolean) => {
  useDashboardStore.setState({ isEditMode: isEdit });
};

export const setDashboard = (dashboard: Dashboard | undefined) => {
  useDashboardStore.setState({ dashboard });
};

export default useDashboardStore;
